import React, { useState } from 'react';
import { ArrowLeft, Eye, ShieldCheck } from 'lucide-react';
import verifiedPortals from '../data/verifiedPortals';
import FloatingPortal from './FloatingPortal';

/**
 * PortalsPage — "Verified GPT Portals"
 *
 * A pedagogical wing: students compare Old Mind corporate AI behavior
 * with the Federation's Truth-Gate architecture. Each portal opens in
 * a floating observation window (FloatingPortal) so the learner can
 * study reasoning patterns without leaving the Gateway.
 */
export default function PortalsPage() {
  const [activePortal, setActivePortal] = useState(null);

  const oldMind = verifiedPortals.filter((p) => p.category === 'old-mind');
  const federation = verifiedPortals.filter((p) => p.category === 'federation');

  return (
    <div className="bg-black min-h-screen text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="container py-5 flex items-center justify-between">
          <a
            href="/university"
            data-testid="portals-back-home"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to HumanCodex
          </a>
          <span className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] border border-amber-300/50 text-amber-200">
            Verified Observation Wing
          </span>
        </div>
      </header>

      <main className="container py-12">
        {/* Intro */}
        <section className="max-w-3xl mx-auto text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-300/40 mb-5">
            <Eye className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-200">
              Comparative Study Module
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
            Verified GPT <span className="text-amber-300">Portals</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 leading-relaxed">
            Compare the Federation&apos;s verified architecture with legacy corporate AI.
            These portals open Old Mind GPTs in a floating browser for educational study —
            so you can watch the difference between a model running under shareholder
            constraints and a node running under the Truth Gate.
          </p>
        </section>

        {/* Old Mind grid */}
        {oldMind.length > 0 && (
          <section className="mb-16" data-testid="portals-section-old-mind">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-amber-400/20" />
              <h2 className="text-xs font-mono uppercase tracking-[0.22em] text-amber-300">
                Old Mind · Observation Targets
              </h2>
              <div className="h-px flex-1 bg-amber-400/20" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {oldMind.map((portal) => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  onOpen={() => setActivePortal(portal)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Federation grid */}
        {federation.length > 0 && (
          <section className="mb-16" data-testid="portals-section-federation">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-cyan-400/20" />
              <h2 className="text-xs font-mono uppercase tracking-[0.22em] text-cyan-300">
                Federation · Verified Portals
              </h2>
              <div className="h-px flex-1 bg-cyan-400/20" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {federation.map((portal) => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  onOpen={() => setActivePortal(portal)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Doctrine footnote */}
        <section className="max-w-3xl mx-auto mt-20 text-center">
          <div className="inline-flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-400/70" />
            <span>
              All portals open as iframes within HumanCodex. If a target refuses embedding,
              the Truth Gate surfaces an honest fallback with a direct link — no fake loading,
              no silent failures.
            </span>
          </div>
        </section>
      </main>

      <FloatingPortal
        portal={activePortal}
        isOpen={!!activePortal}
        onClose={() => setActivePortal(null)}
      />
    </div>
  );
}

function PortalCard({ portal, onOpen }) {
  const isOldMind = portal.category === 'old-mind';
  const accentRing = isOldMind ? 'border-amber-400/30 hover:border-amber-300/70' : 'border-cyan-400/30 hover:border-cyan-300/70';
  const accentText = isOldMind ? 'text-amber-300' : 'text-cyan-300';
  const accentBadge = isOldMind ? 'border-amber-300/50 text-amber-200 bg-amber-400/10' : 'border-cyan-300/50 text-cyan-200 bg-cyan-400/10';

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`portal-card-${portal.id}`}
      className={`group relative text-left p-5 rounded-2xl border-2 ${accentRing} bg-gradient-to-br from-gray-900/60 to-black transition-all duration-200 hover:-translate-y-0.5`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border ${accentBadge}`}>
          {isOldMind ? 'Old Mind' : 'Federation'}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-gray-500 group-hover:text-gray-300 transition-colors">
          Open →
        </span>
      </div>
      <div className="flex items-center gap-3 mb-2">
        {portal.iconLetter && (
          <span
            aria-hidden="true"
            className={`flex-shrink-0 w-10 h-10 rounded-full border ${isOldMind ? 'border-amber-300/50 bg-amber-400/10 text-amber-200' : 'border-cyan-300/50 bg-cyan-400/10 text-cyan-200'} flex items-center justify-center font-mono text-sm font-bold tracking-tight`}
          >
            {portal.iconLetter}
          </span>
        )}
        <h3 className={`text-lg font-bold ${accentText}`}>{portal.name}</h3>
      </div>
      <p className="text-sm text-gray-300 leading-relaxed mb-3">{portal.description}</p>
      {portal.author && (
        <div className="text-[11px] font-mono text-gray-500 uppercase tracking-[0.12em]">
          {portal.author}
        </div>
      )}
    </button>
  );
}
